import { Alert, Button, Col, Form, Input, Popconfirm, Row, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";

// Kurumun tetkik paylaşımı maili: gönderen adı, yanıt adresi, konu ve HTML gövde şablonu. Sağdaki
// önizleme sunucuda, gönderimde kullanılan işleyiciyle örnek verilerle üretilir — kaydetmeden görülür.
export default function HospitalShareEmailSettings({ hospitalId, onSaved }) {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const templateRef = useRef(null);
    const lastFocused = useRef('template');
    const previewSeq = useRef(0);

    const [settings, setSettings] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const emailFromName = Form.useWatch('emailFromName', form);
    const emailFromAddress = Form.useWatch('emailFromAddress', form);
    const subject = Form.useWatch('subject', form);
    const template = Form.useWatch('template', form);

    useEffect(() => {
        (async () => {
            const response = await ApiService.Hospital.GetShareEmailSettings(hospitalId);
            if (response.success) {
                setSettings(response.data);
                form.setFieldsValue({
                    emailFromName: response.data.emailFromName || '',
                    emailFromAddress: response.data.emailFromAddress || '',
                    subject: response.data.subject || '',
                    template: response.data.template || ''
                });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hospitalId]);

    useEffect(() => {
        if (!settings) {
            return;
        }

        const seq = ++previewSeq.current;
        const timer = setTimeout(async () => {
            setPreviewLoading(true);
            const response = await ApiService.Hospital.PreviewShareEmail(hospitalId, {
                emailFromName, emailFromAddress, subject, template
            });
            if (seq !== previewSeq.current) {
                return;
            }
            setPreviewLoading(false);
            if (response.success) {
                setPreview(response.data);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [settings, hospitalId, emailFromName, emailFromAddress, subject, template]);

    // Yer tutucu, son odaklanılan alana (konu ya da gövde) imlecin olduğu yere eklenir.
    const insertPlaceholder = (key) => {
        const field = lastFocused.current;
        const current = form.getFieldValue(field) || '';

        if (field === 'template') {
            const el = templateRef.current?.resizableTextArea?.textArea;
            const start = el?.selectionStart ?? current.length;
            const end = el?.selectionEnd ?? current.length;
            form.setFieldsValue({ template: current.slice(0, start) + key + current.slice(end) });
            requestAnimationFrame(() => {
                if (el) {
                    el.focus();
                    el.selectionStart = el.selectionEnd = start + key.length;
                }
            });
        } else {
            form.setFieldsValue({ [field]: current + key });
        }
    };

    const loadDefaultTemplate = () => {
        form.setFieldsValue({ template: settings.defaultTemplate });
    };

    const save = async (values) => {
        try {
            setSaving(true);
            const response = await ApiService.Hospital.SaveShareEmailSettings(hospitalId, {
                emailFromName: values.emailFromName ?? '',
                emailFromAddress: values.emailFromAddress ?? '',
                subject: values.subject ?? '',
                template: values.template ?? ''
            });
            if (response.success && onSaved) {
                onSaved(hospitalId);
            }
        } finally {
            setSaving(false);
        }
    };

    if (!settings) {
        return <Spin />;
    }

    return (
        <Form form={form} layout="vertical" onFinish={save}>
            <Row gutter={24}>
                <Col xs={24} lg={12}>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={t('Share emails are sent from the system address {{address}}. The recipient sees the sender name below; replies go to the reply address.', { address: settings.systemFromAddress || '-' })}
                    />

                    <Row gutter={12}>
                        <Col span={12}>
                            <Form.Item name="emailFromName" label={t('Sender Name')}>
                                <Input placeholder={settings.hospitalName} maxLength={256} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="emailFromAddress"
                                label={t('Reply Address')}
                                tooltip={t('Replies from recipients go to this address (Reply-To). Empty: replies go to the system address.')}
                                rules={[{ type: 'email', message: t('Invalid email address') }]}
                            >
                                <Input placeholder="radyoloji@hastane.com" maxLength={256} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="subject"
                        label={t('Subject')}
                        extra={t('Empty: {{subject}}', { subject: settings.defaultSubject })}
                    >
                        <Input
                            placeholder={settings.defaultSubject}
                            maxLength={500}
                            onFocus={() => { lastFocused.current = 'subject'; }}
                        />
                    </Form.Item>

                    <div style={{ marginBottom: 8 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {t('Placeholders (click to insert):')}
                        </Typography.Text>
                        <div style={{ marginTop: 4 }}>
                            {settings.placeholders.map(p => (
                                <Tooltip key={p.key} title={p.description}>
                                    <Tag style={{ cursor: 'pointer', marginBottom: 4 }} onClick={() => insertPlaceholder(p.key)}>
                                        {p.key}
                                    </Tag>
                                </Tooltip>
                            ))}
                        </div>
                    </div>

                    <Form.Item
                        name="template"
                        label={
                            <Space>
                                {t('Email Body (HTML)')}
                                <Popconfirm
                                    title={t('The current template will be replaced with the default template.')}
                                    onConfirm={loadDefaultTemplate}
                                >
                                    <Button size="small" type="link">{t('Load default template')}</Button>
                                </Popconfirm>
                            </Space>
                        }
                        extra={t('Empty: the default template is used. If the template has no description or viewer placeholder, they are added above the study cards.')}
                    >
                        <Input.TextArea
                            ref={templateRef}
                            rows={18}
                            placeholder={t('Empty: the default template is used.')}
                            style={{ fontFamily: 'monospace', fontSize: 12 }}
                            onFocus={() => { lastFocused.current = 'template'; }}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                    <Typography.Text strong>{t('Preview (sample data)')}</Typography.Text>
                    <Spin spinning={previewLoading}>
                        {preview && (
                            <div style={{ fontSize: 12, margin: '8px 0', lineHeight: '20px' }}>
                                <div>{t('From')}: <b>{preview.fromName}</b>{preview.fromAddress ? ` <${preview.fromAddress}>` : ''}</div>
                                <div>{t('Replies go to')}: {preview.replyTo || t('not set (system address)')}</div>
                                <div>{t('Subject')}: <b>{preview.subject}</b></div>
                            </div>
                        )}
                        <iframe
                            title="share-email-preview"
                            sandbox=""
                            srcDoc={preview?.html || ''}
                            style={{ width: '100%', height: 620, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}
                        />
                    </Spin>
                </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                    {t('Save')}
                </Button>
            </div>
        </Form>
    );
}
